import type React from 'react';
import { useEffect, useState } from 'react';
import { useTheme } from '@emotion/react';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useLingui } from '@lingui/react';
import { format } from 'date-fns';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Button, FormGroup, Input, Label } from 'src/components/ui';
import Icon from 'src/components/Icon';
import OrgUnitMultiSelect from 'src/components/OrgUnitTreeMultiSelect';
import { isoToDate, dateToISO, getDateFormat, getDateFnsLocale } from './DateWidget';
import Notification from '../Notification';
import Loader from '../Loader';
import useResourceLoader from '../../util/useResourceLoader';
import UWEEntityApi from '../../api/UWEEntityApi';

type TemporalDataType = 'allData' | 'fromDate' | 'untilDate' | 'specificRange' | '';

type TemporalData = {
  type: TemporalDataType;
  fromDate?: string;
  untilDate?: string;
};

type AccessItem = {
  dashboard: string;
  scope: number[];
  temporalData: TemporalData;
  freeText?: string;
};

function isFreeTextAccess(access: AccessItem): boolean {
  return !access.dashboard && !(access.scope || []).length && !!access.freeText;
}

interface OcifAccessRequestListProps {
  formData: AccessItem[];
  schema?: { title?: string; readOnly?: boolean };
  uiSchema?: { 'ui:readonly'?: boolean };
  required?: boolean;
  disabled?: boolean;
  readonly?: boolean;
  onChange: (value: AccessItem[]) => void;
}

const TEMPORAL_OPTIONS: { value: TemporalDataType; label: string }[] = [
  { value: 'allData', label: t`All available data` },
  { value: 'fromDate', label: t`From a date forward` },
  { value: 'untilDate', label: t`Until a date` },
  { value: 'specificRange', label: t`Specific range` },
];

function makeEmptyAccess(): AccessItem {
  return { dashboard: '', scope: [], temporalData: { type: '' } };
}

function extractItems(r: any): any[] {
  return Array.isArray(r) ? r : r?.items ?? [];
}

function OcifAccessRequestList(props: OcifAccessRequestListProps) {
  const {
    formData,
    schema: { title, readOnly: schemaReadOnly } = {},
    uiSchema: { 'ui:readonly': uiReadonly } = {},
    required,
    disabled,
    readonly,
    onChange: propOnChange,
  } = props;
  const isDisabled = !!(disabled || readonly || uiReadonly || schemaReadOnly);

  const accesses = formData || [];

  const [dashboards, loadingDashboards, errorLoadingDashboards] = useResourceLoader<any[]>(
    () =>
      UWEEntityApi.getWorkTray({ resource: 'manage/reports/dashboards/list', offset: 0, size: 1000, sortBy: 'name' })
        .then(extractItems)
        .catch(() => []),
    []
  );

  const [orgUnits, loadingOrgUnits, errorLoadingOrgUnits] = useResourceLoader<any[]>(
    () =>
      UWEEntityApi.getWorkTray({ resource: 'manage/organizationalunit', offset: 0, size: 1000, all: true } as any)
        .then(extractItems)
        .catch(() => []),
    []
  );

  const loading = loadingDashboards || loadingOrgUnits;
  const error = errorLoadingDashboards || errorLoadingOrgUnits;

  function updateAccess(idx: number, patch: Partial<AccessItem>) {
    propOnChange(accesses.map((a, i) => (i === idx ? { ...a, ...patch } : a)));
  }

  function addAccess() {
    propOnChange([...accesses, makeEmptyAccess()]);
  }

  function removeAccess(idx: number) {
    propOnChange(accesses.filter((_, i) => i !== idx));
  }

  return (
    <div className="form-group">
      {title ? (
        <label className="control-label">
          {title}
          {!readonly && <span className="required"> *</span>}
        </label>
      ) : null}

      {loading ? (
        <Loader dots inline>
          <Trans>Loading</Trans>
        </Loader>
      ) : error ? (
        <Notification color="danger">
          <div>{error.message}</div>
        </Notification>
      ) : (
        <>
          {accesses.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 12 }}>
              {accesses.map((access, idx) => (
                <AccessRow
                  key={idx}
                  index={idx}
                  access={access}
                  dashboards={dashboards || []}
                  orgUnits={orgUnits || []}
                  disabled={isDisabled}
                  onChange={patch => updateAccess(idx, patch)}
                  onRemove={() => removeAccess(idx)}
                />
              ))}
            </div>
          ) : (
            <div className="text-muted" style={{ fontStyle: 'italic', marginBottom: 12 }}>
              <Trans>No accesses added yet.</Trans>
            </div>
          )}

          {!isDisabled && (
            <Button type="button" color="primary" size="sm" outline onClick={addAccess}>
              <Icon icon="Plus" weight="regular" /> <Trans>Add Access</Trans>
            </Button>
          )}
        </>
      )}
    </div>
  );
}

function AccessRow({
  index,
  access,
  dashboards,
  orgUnits,
  disabled,
  onChange,
  onRemove,
}: {
  index: number;
  access: AccessItem;
  dashboards: any[];
  orgUnits: any[];
  disabled: boolean;
  onChange: (patch: Partial<AccessItem>) => void;
  onRemove: () => void;
}) {
  const theme = useTheme();
  const { i18n } = useLingui();
  const temporalData = access.temporalData || { type: '' };
  const needsFrom = temporalData.type === 'fromDate' || temporalData.type === 'specificRange';
  const needsUntil = temporalData.type === 'untilDate' || temporalData.type === 'specificRange';

  function setTemporalType(type: TemporalDataType) {
    onChange({ temporalData: { type } });
  }

  function setTemporalDate(field: 'fromDate' | 'untilDate', value: string) {
    onChange({ temporalData: { ...temporalData, [field]: value } });
  }

  function formatAccessDate(iso?: string) {
    const date = isoToDate(iso);
    return date ? format(date, getDateFormat(i18n.locale), { locale: getDateFnsLocale(i18n.locale) }) : '';
  }

  const [isFreeText, setIsFreeText] = useState<boolean>(() => isFreeTextAccess(access));

  function toggleFreeText(next: boolean) {
    setIsFreeText(next);
    if (next) {
      onChange({ dashboard: '', scope: [], freeText: access.freeText || '' });
    } else {
      onChange({ freeText: '' });
    }
  }

  const dashboardName = dashboards.find((d: any) => d.nativeUuid === access.dashboard)?.name || '';
  const orgUnitNames = orgUnits.filter((o: any) => (access.scope || []).includes(o.id)).map((o: any) => o.name);
  const temporalLabel = TEMPORAL_OPTIONS.find(o => o.value === temporalData.type)?.label || '';

  const dateRangeText = needsFrom && needsUntil
    ? `${formatAccessDate(temporalData.fromDate) || '—'} – ${formatAccessDate(temporalData.untilDate) || '—'}`
    : needsFrom
      ? formatAccessDate(temporalData.fromDate) || '—'
      : needsUntil
        ? formatAccessDate(temporalData.untilDate) || '—'
        : '—';

  return (
    <div
      style={{
        display: 'flex',
        border: '1px solid #dee2e6',
        borderRadius: '0.5rem',
        overflow: 'hidden',
        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9f9f9',
      }}
    >
      <div
        style={{
          flex: '0 0 auto',
          width: 36,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: (theme as any)?.primaryMain || '#091f68',
          color: (theme as any)?.primaryTextMain || '#fff',
          fontWeight: 600,
        }}
      >
        {index + 1}
      </div>

      <div style={{ flex: 1, padding: '1rem', position: 'relative' }}>
        {!disabled && (
          <Button
            type="button"
            className="btn-xs btn-outline-danger border-0"
            onClick={onRemove}
            title={t`Remove access`}
            style={{ position: 'absolute', top: '0.5rem', right: '0.5rem' }}
          >
            <Icon icon="X" weight="regular" size={12} />
          </Button>
        )}

        {disabled ? (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            {isFreeText ? (
              <ReadonlyField label={<Trans>Request Details</Trans>} value={access.freeText || '—'} />
            ) : (
              <>
                <ReadonlyField label={<Trans>Report / Dashboard</Trans>} value={dashboardName || '—'} />
                <ReadonlyField label={<Trans>Scope — Institutions</Trans>} value={orgUnitNames.length ? orgUnitNames.join(', ') : '—'} />
              </>
            )}
            <ReadonlyField label={<Trans>Temporal Data Scope</Trans>} value={temporalLabel || '—'} />
            <ReadonlyField label={<Trans>Date Range</Trans>} value={dateRangeText} />
          </div>
        ) : (
          <>
            {isFreeText ? (
              <FormGroup>
                <Label className="control-label">
                  <Trans>Describe what you need</Trans>
                </Label>
                <Input
                  type="textarea"
                  rows={3}
                  value={access.freeText || ''}
                  onChange={e => onChange({ freeText: e.target.value })}
                  placeholder={t`Describe the report and scope you need`}
                />
                <div style={{ fontSize: '0.85em', marginTop: 4 }}>
                  <Button type="button" color="link" size="sm" className="p-0" onClick={() => toggleFreeText(false)}>
                    <Trans>Back to Report and Scope selection</Trans>
                  </Button>
                </div>
              </FormGroup>
            ) : (
              <>
                <FormGroup>
                  <Label className="control-label">
                    <Trans>Report / Dashboard</Trans>
                  </Label>
                  <Input type="select" value={access.dashboard || ''} onChange={e => onChange({ dashboard: e.target.value })}>
                    <option value="">{t`-- Select an option --`}</option>
                    {dashboards.map((d: any) => (
                      <option key={d.nativeUuid} value={d.nativeUuid}>
                        {d.name}
                      </option>
                    ))}
                  </Input>
                </FormGroup>

                <FormGroup>
                  <Label className="control-label">
                    <Trans>Scope — Institutions</Trans>
                  </Label>
                  <OrgUnitMultiSelect options={orgUnits} selectedIds={access.scope || []} onChange={scope => onChange({ scope })} />
                </FormGroup>

                <div className="text-muted" style={{ fontSize: '0.85em', marginTop: -8, marginBottom: '1rem' }}>
                  <Trans>Can't find what you're looking for?</Trans>{' '}
                  <Button type="button" color="link" size="sm" className="p-0" onClick={() => toggleFreeText(true)}>
                    <Trans>Let us know what you need and we'll review your request</Trans>
                  </Button>
                </div>
              </>
            )}

            <FormGroup>
              <Label className="control-label">
                <Trans>Temporal Data Scope</Trans>
              </Label>
              <Input type="select" value={temporalData.type || ''} onChange={e => setTemporalType(e.target.value as TemporalDataType)}>
                <option value="">{t`-- Select an option --`}</option>
                {TEMPORAL_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Input>
            </FormGroup>

            {(needsFrom || needsUntil) && (
              <div style={{ display: 'grid', gridTemplateColumns: needsFrom && needsUntil ? '1fr 1fr' : '1fr', gap: '1rem' }}>
                {needsFrom && (
                  <FormGroup>
                    <Label className="control-label">
                      <Trans>From</Trans>
                    </Label>
                    <AccessDatePicker value={temporalData.fromDate || ''} onChange={v => setTemporalDate('fromDate', v)} />
                  </FormGroup>
                )}
                {needsUntil && (
                  <FormGroup>
                    <Label className="control-label">
                      <Trans>Until</Trans>
                    </Label>
                    <AccessDatePicker value={temporalData.untilDate || ''} onChange={v => setTemporalDate('untilDate', v)} />
                  </FormGroup>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function ReadonlyField({ label, value }: { label: React.ReactNode; value: React.ReactNode }) {
  return (
    <FormGroup>
      <Label className="control-label">{label}</Label>
      <div className="form-control-plaintext">{value}</div>
    </FormGroup>
  );
}

let _datePickerInstanceCounter = 0;

function AccessDatePicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { i18n } = useLingui();
  const dateFormat = getDateFormat(i18n.locale);
  const locale = getDateFnsLocale(i18n.locale);
  const [current, setCurrent] = useState<Date | null>(() => isoToDate(value));
  const [portalId] = useState(() => `datepicker-portal-access-${++_datePickerInstanceCounter}`);

  useEffect(() => {
    setCurrent(isoToDate(value));
  }, [value]);

  useEffect(() => {
    const elem = document.createElement('div');
    elem.id = portalId;
    document.body.appendChild(elem);
    return () => {
      document.body.removeChild(elem);
    };
  }, [portalId]);

  return (
    <DatePicker
      locale={locale}
      dateFormat={dateFormat}
      selected={current}
      onChange={(date: Date | null) => {
        setCurrent(date);
        onChange(date ? dateToISO(date) : '');
      }}
      placeholderText={t`Select a date`}
      className="form-control custom pl-8 w-full"
      wrapperClassName="block"
      showPopperArrow={false}
      showIcon
      icon={<Icon icon="Calendar" className="fa-regular h-full" style={{ zIndex: 99 }} />}
      portalId={portalId}
      popperProps={{ strategy: 'fixed' }}
      popperPlacement="bottom-start"
      toggleCalendarOnIconClick
      showYearDropdown
      scrollableYearDropdown
    />
  );
}

export default OcifAccessRequestList;
